import { useEffect, useState } from "react";
import { DesignTokens, Table, Modal, Button } from "melco-ui";
import { Pagination } from "antd";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { SELECTED_BG, SELECTED_ACCENT } from "./summitTheme";

const { colors, spacings } = DesignTokens;

// Selectable page sizes — a single source of truth so the dropdown
// options and the default can't drift out of sync.
const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

// WHAT: a footer row that hosts antd's own Pagination control (numbered
// pages: 1 2 3 4 5 …), imported directly from `antd` rather than
// whatever melco-ui re-exports.
// WHY: two different attempts at wiring Table's *built-in* pagination
// (nested pagination.onChange, then Table's top-level onChange — both
// documented antd patterns) still didn't correctly advance pages, and
// melco-ui's own Pagination re-export was untested territory on top of
// that. Going straight to antd's Pagination — confirmed to be an actual
// dependency of this project via the `antd/lib/table/interface` types
// already used in this codebase's shared table hooks — removes a layer
// of uncertainty: it's a small, standalone component with no coupling
// to Table's own (buggy) internal pagination logic.
const PaginationFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: ${spacings.small}px ${spacings.middle}px;
`;

// Small bordered mono badge for the error code (e.g. WARN_UPDATE_PENDING),
// matching the reference screenshot's styling under the error message text.
//
// Colors are the same SELECTED_BG/SELECTED_ACCENT pair already used
// elsewhere in this feature for selected rows — confirmed legible against
// each other (sampled directly from a working reference screenshot), unlike
// the earlier #fff5f5/colors.main.negative combo which turned out to be
// unreadable once actually rendered.
const ErrorCodeBadge = styled.span`
    display: inline-block;
    margin-top: 3px;
    padding: 2px 8px;
    font-size: 11px;
    font-weight: 700;
    color: ${SELECTED_ACCENT};
    background: ${SELECTED_BG};
    border-radius: 4px;
`;

type LogsTableProps = {
    dataSource: any[];
    loading?: boolean;
};

export const LogsTable: React.FC<LogsTableProps> = ({ dataSource, loading }) => {
    const { t } = useTranslation();
    const [modalVisible, setModalVisible] = useState(false);
    const [modalContent, setModalContent] = useState<string | null>(null);

    // WHAT: current page + page size, owned entirely by us.
    // WHY plain state instead of anything shaped for Table's pagination
    // API: Table's own pagination is disabled below (`pagination={false}`),
    // so nothing here needs to match antd's TablePaginationConfig shape —
    // it only has to make sense to the standalone Pagination control
    // below, which we drive directly via its (page, pageSize) callback.
    const [current, setCurrent] = useState(1);
    const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

    const total = dataSource.length;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));

    // GOTCHA: if a status filter shrinks the data (or a newly selected
    // machine simply has fewer logs) while the rep is sitting on a later
    // page, that page can end up past the new last page. Clamp back to
    // the last valid page instead of showing an empty page.
    useEffect(() => {
        setCurrent((prev) => Math.min(prev, pageCount));
    }, [pageCount]);

    // Logs are fetched in one request rather than page-by-page from the
    // server, so we slice the current page out of the full array
    // ourselves and hand Table only that slice — Table never sees more
    // than `pageSize` rows at a time.
    const pagedData = dataSource.slice((current - 1) * pageSize, current * pageSize);

    // Pagination's onChange fires with the new page and page size
    // together, whether the rep clicked a page number or changed the
    // size — no need to distinguish the two cases here.
    const handlePaginationChange = (nextPage: number, nextPageSize: number) => {
        setCurrent(nextPage);
        setPageSize(nextPageSize);
    };

    const openModal = (url: string) => {
        setModalContent(url);
        setModalVisible(true);
    };

    const isErrorRecord = (record: any) =>
        record?.category?.toString().toUpperCase() === "ERROR" ||
        record?.status?.toString().toUpperCase() === "ERROR";

    const columns = [
        {
            title: ("Date/Time"),
            dataIndex: "created_date",
            key: "created_date",
            render: (_: string, record: any) => (
                <span style={{ fontSize: 13 }}>
                    {record.created_date ? new Date(record.created_date).toLocaleString() : ""}
                </span>
            ),
        },
        {
            title: ("Status"),
            // dataIndex: "status",
            key: "status",
            render: (rowObj: any) => {
                const status = rowObj?.status?.toString().toUpperCase();
                //  return <>{(status)}</>
                if (status === "ERROR") {
                    return (<div style={{ color: "red" }}>{status}</div>)
                }
                else if (status === "SUCCESS") {
                    return (<div style={{ color: "green" }}>{status}</div>)
                }
                else {
                    return (<div >{status}</div>)
                }
            }
        },

        {
            title: "Message",
            dataIndex: "message",
            key: "message",
            render: (_: string, record: any) => {
                const status = record?.status?.toString().toUpperCase();

                if (status === "ERROR") {
                    return (
                        <div style={{ fontSize: 13 }}>
                            <div>{record?.error_message}</div>
                            {record?.error && <ErrorCodeBadge>{record.error}</ErrorCodeBadge>}
                        </div>
                    );
                }

                if (status === "SUCCESS") {
                    return <span style={{ fontSize: 13, color: colors.gray.dark }}>Scan Successfull</span>;
                }

                return <span style={{ fontSize: 13, color: colors.gray.dark }}>{record?.error_message ?? ""}</span>;
            },
        },
        {
            title: t("summit.table.columns.view"),
            key: "view",
            render: (_: string, record: any) =>
                record.url ? (
                    <Button type="primary" onClick={() => openModal(record.url)}>
                        View
                    </Button>
                ) : null,
        },
    ];

    return (
        <>
            <Table
                columns={columns}
                dataSource={pagedData}
                // WHAT: device_id + created_date + the row's position in
                // the currently-rendered page.
                // WHY the index is necessary here, not just decorative:
                // device_id + created_date alone isn't guaranteed unique —
                // multiple log entries can share the same device and the
                // same timestamp (e.g. two events logged within the same
                // second). Duplicate React keys among sibling rows cause
                // React to misrender/reuse row instances between renders,
                // which can look exactly like "pagination is broken" even
                // when the underlying slicing logic is correct. There's no
                // real unique id field on these records to key off instead,
                // so the index — guaranteed unique within whatever page is
                // currently rendered — is the safe fallback.
                rowKey={(record: any, index?: number) =>
                    `${record.device_id}-${record.created_date}-${index}`
                }
                pagination={false}
                loading={loading}
            />

            <PaginationFooter>
                <Pagination
                    current={current}
                    pageSize={pageSize}
                    total={total}
                    showSizeChanger
                    pageSizeOptions={PAGE_SIZE_OPTIONS.map(String)}
                    onChange={handlePaginationChange}
                    onShowSizeChange={handlePaginationChange}
                />
            </PaginationFooter>

            <Modal
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width="min(90%, 800px)"
                bodyStyle={{
                    padding: 0,
                    height: "95vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
                closable={true}
                destroyOnClose={true}
                maskClosable={false}
                centered
            >
                {modalContent && (
                    <iframe
                        src={modalContent}
                        style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                            maxWidth: 800
                        }}
                    />
                )}
            </Modal>
        </>
    );
};
